
import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import ModalContent from './modalContent/ModalContent';
import { useBusinessForm } from './hooks/useBusinessForm';
import { useFormSections } from './hooks/useFormSections';
import { useBusinessSearch } from './hooks/useBusinessSearch';
import { useBusinessSubmit } from './hooks/useBusinessSubmit';
import { useIsMobile } from '@/hooks/use-mobile';

interface AddBusinessModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: (businessId: string) => void;
  portfolioId?: string;
  portfolioName?: string;
}

const AddBusinessModal: React.FC<AddBusinessModalProps> = ({ 
  open, 
  onOpenChange,
  onSuccess,
  portfolioId = '',
  portfolioName = ''
}) => {
  const isMobile = useIsMobile();
  
  const {
    businessDetails,
    calendarOpen,
    setCalendarOpen,
    selectedDate,
    setSelectedDate,
    shortNameManuallyEdited,
    nameExceeds30Chars,
    shortNameLength,
    handleInputChange,
    handleSelectChange,
    handleDateSelect,
    handleWebsiteBlur,
    resetBusinessDetails,
    updateBusinessDetails,
    errors
  } = useBusinessForm();

  const {
    formSection,
    setFormSection,
    basicInfoComplete,
    addressComplete,
    industryComplete,
    basicInfoEdited,
    addressEdited,
    industryEdited,
    basicInfoHasChanges,
    addressHasChanges,
    industryHasChanges,
    saveBasicInfo,
    saveAddress,
    saveIndustry,
    showAddressSection,
    showIndustrySection,
    showSubmitButton
  } = useFormSections(businessDetails);

  const { 
    handleBusinessSelect, 
    handleCreateNew: handleCreateNewFromHook
  } = useBusinessSearch(updateBusinessDetails, setSelectedDate, setFormSection);

  // Adapting the function signature to match what's expected
  const handleCreateNew = () => {
    // Since BusinessSearchField will call this with the search term, 
    // but our hook version doesn't need it since it gets called from another context,
    // we adapt the function signature
    handleCreateNewFromHook();
  };

  // Create a submission handler using the useBusinessSubmit hook
  const { handleSubmit: submitHandler } = useBusinessSubmit(portfolioId, portfolioName, onOpenChange);
  
  // Create the actual handleSubmit function to pass to components
  const handleSubmit = () => {
    const submitter = submitHandler();
    submitter(businessDetails);
    if (onSuccess && typeof onSuccess === 'function') {
      // If there's an onSuccess callback, we'll call it with a dummy ID for now
      // The real ID will come from the submitter function in the future
      setTimeout(() => onSuccess('new-business-id'), 100);
    }
  };

  const handleModalOpenChange = (open: boolean) => {
    if (!open) {
      resetBusinessDetails();
      setFormSection('search');
    }
    onOpenChange(open);
  };

  return (
    <Dialog open={open} onOpenChange={handleModalOpenChange}>
      <DialogContent className="sm:max-w-[600px] p-0 gap-0">
        <DialogHeader className="p-6 pb-2">
          <DialogTitle className="text-xl font-semibold">
            {formSection === 'search' ? 'Add Business' : 'Business Details'}
          </DialogTitle>
        </DialogHeader>
        
        <ModalContent
          formSection={formSection}
          businessDetails={businessDetails}
          handleInputChange={handleInputChange}
          handleSelectChange={handleSelectChange}
          selectedDate={selectedDate}
          calendarOpen={calendarOpen}
          setCalendarOpen={setCalendarOpen}
          handleDateSelect={handleDateSelect}
          handleWebsiteBlur={handleWebsiteBlur}
          nameExceeds30Chars={nameExceeds30Chars}
          shortNameLength={shortNameLength}
          shortNameManuallyEdited={shortNameManuallyEdited}
          showAddressSection={showAddressSection}
          showIndustrySection={showIndustrySection}
          showSubmitButton={showSubmitButton}
          handleSubmit={handleSubmit}
          handleOpenChange={handleModalOpenChange}
          handleBusinessSelect={handleBusinessSelect}
          handleCreateNew={handleCreateNew}
          isMobile={isMobile}
          basicInfoComplete={basicInfoComplete}
          addressComplete={addressComplete}
          industryComplete={industryComplete}
          basicInfoEdited={basicInfoEdited}
          addressEdited={addressEdited}
          industryEdited={industryEdited}
          basicInfoHasChanges={basicInfoHasChanges}
          addressHasChanges={addressHasChanges}
          industryHasChanges={industryHasChanges}
          onSaveBasicInfo={saveBasicInfo}
          onSaveAddress={saveAddress}
          onSaveIndustry={saveIndustry}
          errors={errors}
        />
      </DialogContent>
    </Dialog>
  );
};

export default AddBusinessModal;
